// src/pages/Activity.jsx
//
// God-view feed of everything Claire is doing across all platforms.
// All interactions across all leads in one unified timeline -- newest first,
// filterable by platform and direction. Each row links back to the lead.
//
// Note: currently only shows interactions tied to existing leads, because
// the pollers only record things when they can match an inbound message to
// a lead.email. Pre-lead social conversations (mentions from strangers,
// replies from people not yet in Leads) aren't captured yet -- that would
// need a separate pre-lead inbox model.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const PLATFORM_STYLES = {
  gmail: "bg-blue-100 text-blue-800",
  twitter: "bg-sky-100 text-sky-800",
  discord: "bg-purple-100 text-purple-800",
  manual: "bg-gray-100 text-gray-700",
};

function formatDateTime(v) {
  return v ? new Date(v).toLocaleString() : "—";
}

const PAGE_SIZE = 50;

export default function Activity() {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [platform, setPlatform] = useState("all");
  const [direction, setDirection] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [platform, direction]);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      setLoading(true);
      setError("");
      let q = supabase
        .from("interactions")
        .select("id, platform, type, direction, content, outcome, score_delta, occurred_at, leads(id, name, status)", { count: "exact" })
        .order("occurred_at", { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      if (platform !== "all") q = q.eq("platform", platform);
      if (direction !== "all") q = q.eq("direction", direction);
      const { data, error: fetchErr, count } = await q;
      if (cancelled) return;
      if (fetchErr) setError(fetchErr.message);
      else {
        setInteractions(data || []);
        setTotal(count || 0);
      }
      setLoading(false);
    }
    fetch();
    return () => { cancelled = true; };
  }, [platform, direction, page]);

  const platforms = ["all", "gmail", "twitter", "discord", "manual"];

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Activity</h1>
            {total > 0 && <p className="text-sm text-gray-500">{total} interactions total</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded border p-2 text-sm">
              {platforms.map((p) => (
                <option key={p} value={p}>{p === "all" ? "All platforms" : p}</option>
              ))}
            </select>
            <select value={direction} onChange={(e) => setDirection(e.target.value)} className="rounded border p-2 text-sm">
              <option value="all">Both directions</option>
              <option value="inbound">Inbound only</option>
              <option value="outbound">Outbound only</option>
            </select>
          </div>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          Everything Claire has sent or received across Gmail, Twitter, Discord, and logged manual touchpoints.
          Conversations with people not yet in Leads aren't captured here yet.
        </p>

        {error ? <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : interactions.length === 0 ? (
          <div className="rounded bg-white p-6 text-center text-sm text-gray-500 shadow">
            No interactions yet.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {interactions.map((i) => (
              <div
                key={i.id}
                className="rounded bg-white p-4 shadow border-l-4"
                style={{ borderColor: i.direction === "inbound" ? "#22c55e" : "#3b82f6" }}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className={`rounded px-2 py-0.5 font-medium ${PLATFORM_STYLES[i.platform] || "bg-gray-100 text-gray-600"}`}>
                    {i.platform}
                  </span>
                  <span>{i.type}</span>
                  <span>·</span>
                  <span>{i.direction === "inbound" ? "← from them" : "→ from Claire"}</span>
                  {i.score_delta != null && i.score_delta !== 0 && (
                    <span className={`font-medium ${i.score_delta > 0 ? "text-green-600" : "text-red-600"}`}>
                      {i.score_delta > 0 ? `+${i.score_delta}` : i.score_delta} pts
                    </span>
                  )}
                  <span className="ml-auto text-gray-400">{formatDateTime(i.occurred_at)}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  {i.leads ? (
                    <Link to={`/leads/${i.leads.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                      {i.leads.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400">Unknown</span>
                  )}
                  {i.leads?.status && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{i.leads.status}</span>
                  )}
                </div>
                {i.content ? (
                  <div className="mt-1 whitespace-pre-wrap text-sm text-gray-700 line-clamp-4">{i.content}</div>
                ) : null}
                {i.outcome ? (
                  <div className="mt-1 text-xs text-gray-400 italic">{i.outcome}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              ← Newer
            </button>
            <span className="text-sm text-gray-500">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <button
              type="button"
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Older →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
